import { z } from 'zod'
import { oc } from '@orpc/contract'
import { soapNoteSchema, visitContextSchema } from '../soap-note-payload.contract'
import { addendumReasonSchema } from './progress-note-addendum.contract'

/**
 * The progress-note READ (PD-1547, B4). Writes have been /v2 for a while
 * (`visitSaveSoapNoteDraft`, `visitLockProgressNote`, the addenda); the web still
 * reads notes straight from Medplum, and the mobile app may not. This returns the
 * encounter's current note as the same `SoapNote` DTO the writes take, plus the
 * lock state, the addenda and the write context, so a client can review, edit,
 * save and lock without ever seeing a FHIR resource.
 */
export const progressNoteAddendumSchema = z.object({
  addendumId: z.string(),
  content: z.string(),
  reason: addendumReasonSchema.nullable(),
  signed: z.boolean(),
  /** When signed; the draft's last save date otherwise. */
  date: z.string().nullable(),
  authorDisplay: z.string().nullable(),
})
export type ProgressNoteAddendum = z.infer<typeof progressNoteAddendumSchema>

export const progressNoteSchema = z.object({
  documentReferenceId: z.string(),
  encounterId: z.string(),
  /** `final` and `amended` are the signed states — see `signed-note-guard.ts`. */
  docStatus: z.enum(['preliminary', 'final', 'amended']),
  locked: z.boolean(),
  /** An AI draft nobody has opened yet (PD-1330's auto-created note). */
  unreviewedAiDraft: z.boolean(),
  /** DocumentReference.date — the note's "Added on" date. */
  date: z.string().nullable(),
  /** When the note was attested; null while preliminary. */
  lockedAt: z.string().nullable(),
  lockedBy: z.string().nullable(),
  soapNote: soapNoteSchema,
  /**
   * What `visitSaveSoapNoteDraft` needs alongside the note — the stored author
   * context, so a save from a client that has none of these ids (mobile) keeps the
   * note's attribution and scribe session.
   */
  visitContext: visitContextSchema,
  addenda: z.array(progressNoteAddendumSchema),
})
export type ProgressNote = z.infer<typeof progressNoteSchema>

/** The plan of the patient's most recent locked note before this visit. */
export const previousPlanSchema = z.object({
  documentReferenceId: z.string(),
  encounterId: z.string().nullable(),
  date: z.string().nullable(),
  plan: z.string(),
})
export type PreviousPlan = z.infer<typeof previousPlanSchema>

export const getProgressNoteResponseSchema = z.object({
  /** Null when the encounter has no readable note yet. */
  note: progressNoteSchema.nullable(),
  previousPlan: previousPlanSchema.nullable(),
})
export type GetProgressNoteResponse = z.infer<typeof getProgressNoteResponseSchema>

export const visitGetProgressNote = oc
  .route({ method: 'GET', path: '/v2/visits/{encounterId}/progress-note' })
  .input(z.object({ encounterId: z.string() }))
  .output(getProgressNoteResponseSchema)
